import type { jsPDF } from "jspdf";

const PDF_BG = "#ffffff";
const PDF_TEXT = "#000000";
const PDF_MUTED = "#666666";
const PDF_BORDER = "#e0e0e0";
const PDF_HEADING_BG = "#f5f5f5";

const setStyle = (el: Element, styles: Partial<CSSStyleDeclaration>) => {
  Object.assign((el as HTMLElement).style, styles);
};

export const loadPdfLogo = (): Promise<HTMLImageElement> =>
  new Promise((resolve) => {
    const img = document.createElement("img");
    img.crossOrigin = "anonymous";
    const base = process.env.PUBLIC_URL ?? "";
    img.alt = "INARI";
    setStyle(img, {
      width: "64px",
      height: "64px",
      display: "block",
      objectFit: "contain",
      flexShrink: "0",
    });

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve(img);
    };

    img.onload = finish;
    img.onerror = () => {
      if (img.src.endsWith(".png")) {
        img.src = `${base}/logo.svg`;
        return;
      }
      finish();
    };
    img.src = `${base}/logo.png`;
    window.setTimeout(finish, 3000);
  });

export const applyPdfLightTheme = (root: HTMLElement): void => {
  setStyle(root, {
    background: PDF_BG,
    color: PDF_TEXT,
    overflow: "visible",
    maxHeight: "none",
    height: "auto",
  });

  root.querySelectorAll(".detail-label").forEach((el) => {
    setStyle(el, { color: PDF_MUTED });
  });

  root.querySelectorAll(".detail-value").forEach((el) => {
    setStyle(el, { color: PDF_TEXT });
  });

  root.querySelectorAll(".detail-row").forEach((el) => {
    setStyle(el, { color: PDF_TEXT });
  });

  root.querySelectorAll(".detail-report-tasks").forEach((el) => {
    setStyle(el, {
      maxHeight: "none",
      height: "auto",
      overflow: "visible",
      marginTop: "8px",
    });
  });

  const table = root.querySelector(".detail-tasks-table");
  if (!table) return;

  setStyle(table, {
    color: PDF_TEXT,
    background: PDF_BG,
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    fontSize: "11px",
  });

  table.querySelectorAll("th").forEach((el) => {
    setStyle(el, {
      color: PDF_MUTED,
      background: PDF_HEADING_BG,
      borderBottom: `1px solid ${PDF_BORDER}`,
      position: "static",
      top: "auto",
      zIndex: "auto",
      boxShadow: "none",
      borderRadius: "0",
      padding: "8px 6px",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      whiteSpace: "normal",
    });
  });

  table.querySelectorAll("td").forEach((el) => {
    setStyle(el, {
      color: PDF_TEXT,
      background: PDF_BG,
      borderBottom: `1px solid ${PDF_BORDER}`,
      padding: "8px 6px",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      whiteSpace: "normal",
      verticalAlign: "top",
    });
  });

  table.querySelectorAll("tr.detail-task-row--green td").forEach((el) => {
    setStyle(el, {
      background: "#e8f5e9",
      borderBottomColor: "#c8e6c9",
      color: PDF_TEXT,
    });
  });

  table.querySelectorAll("tr.detail-task-row--red td").forEach((el) => {
    setStyle(el, {
      background: "#ffebee",
      borderBottomColor: "#ffcdd2",
      color: PDF_TEXT,
    });
  });

  table.querySelectorAll("tr.detail-task-row--yellow td").forEach((el) => {
    setStyle(el, {
      background: "#fff8e1",
      borderBottomColor: "#ffe082",
      color: PDF_TEXT,
    });
  });
};

export const PDF_EXPORT_COLORS = {
  bg: PDF_BG,
  text: PDF_TEXT,
} as const;

export const PDF_HTML2CANVAS_SCALE = 1.5;

export const PDF_JPEG_QUALITY = 0.92;

const canvasToJpeg = (canvas: HTMLCanvasElement, quality = PDF_JPEG_QUALITY): string =>
  canvas.toDataURL("image/jpeg", quality);

const findPageBreakY = (
  preferredEnd: number,
  canvasHeight: number,
  breakPoints: number[]
): number => {
  if (preferredEnd >= canvasHeight) return canvasHeight;
  const minEnd = Math.floor(preferredEnd * 0.7);
  let best = preferredEnd;
  for (let i = breakPoints.length - 1; i >= 0; i -= 1) {
    const point = breakPoints[i];
    if (point <= preferredEnd && point >= minEnd) {
      best = point;
      break;
    }
  }
  return Math.max(1, Math.min(canvasHeight, best));
};

export const collectTableRowBreakPoints = (
  root: HTMLElement,
  scale: number
): number[] => {
  const rootRect = root.getBoundingClientRect();
  const points: number[] = [];
  root.querySelectorAll(".detail-tasks-table tr").forEach((row) => {
    const rect = (row as HTMLElement).getBoundingClientRect();
    const bottom = Math.round((rect.bottom - rootRect.top) * scale);
    if (bottom > 0) points.push(bottom);
  });
  return points;
};

export const addCanvasPagesToPdf = (
  doc: jsPDF,
  source: HTMLCanvasElement,
  breakPoints: number[] = []
): void => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginMm = 8;
  const imgWidthMm = pageWidth - marginMm * 2;
  const usablePageHeightMm = pageHeight - marginMm * 2;
  const fullHeightMm = (source.height * imgWidthMm) / source.width;

  const paintPageBackground = () => {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
  };

  if (fullHeightMm <= usablePageHeightMm) {
    paintPageBackground();
    doc.addImage(
      canvasToJpeg(source),
      "JPEG",
      marginMm,
      marginMm,
      imgWidthMm,
      fullHeightMm,
      undefined,
      "FAST"
    );
    return;
  }

  const pageHeightPx = Math.max(1, Math.floor((usablePageHeightMm * source.width) / imgWidthMm));
  const sliceCanvas = document.createElement("canvas");
  const sliceCtx = sliceCanvas.getContext("2d");
  if (!sliceCtx) return;

  let offsetY = 0;
  let pageIndex = 0;

  while (offsetY < source.height) {
    const preferredEnd = Math.min(offsetY + pageHeightPx, source.height);
    const breakEnd = findPageBreakY(preferredEnd, source.height, breakPoints);
    const sliceHeightPx =
      breakEnd > offsetY ? breakEnd - offsetY : Math.min(pageHeightPx, source.height - offsetY);

    sliceCanvas.width = source.width;
    sliceCanvas.height = sliceHeightPx;
    sliceCtx.fillStyle = PDF_BG;
    sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(
      source,
      0,
      offsetY,
      source.width,
      sliceHeightPx,
      0,
      0,
      source.width,
      sliceHeightPx
    );

    const sliceHeightMm = (sliceHeightPx * imgWidthMm) / source.width;
    if (pageIndex > 0) doc.addPage();
    paintPageBackground();
    doc.addImage(
      canvasToJpeg(sliceCanvas),
      "JPEG",
      marginMm,
      marginMm,
      imgWidthMm,
      sliceHeightMm,
      undefined,
      "FAST"
    );

    offsetY += sliceHeightPx;
    pageIndex += 1;
  }
};
